import React, { Component } from 'react';
import { bool, func, object, string } from 'prop-types';
import classNames from 'classnames';

import { injectIntl, intlShape } from '../../../util/reactIntl';
import { propTypes } from '../../../util/types';
import { convertPrice, formatMoney } from '../../../util/currency';
import { ensureListing } from '../../../util/data';

import css from './SearchMapPriceLabel.module.css';
// import { staticExchangeRates } from '../../../config/configStripe';

const HOVER_DELAY = 250; // milliseconds
const MOBILE_BREAKPOINT = 1024;

class SearchMapPriceLabel extends Component {
  constructor(props) {
    super(props);
    this.hoverTimeout = null;
    this.isMobile = typeof window !== 'undefined' && window.innerWidth <= MOBILE_BREAKPOINT;
  }

  componentWillUnmount() {
    if (this.hoverTimeout) {
      clearTimeout(this.hoverTimeout);
    }
  }

  shouldComponentUpdate(nextProps) {
    const currentListing = ensureListing(this.props.listing);
    const nextListing = ensureListing(nextProps.listing);
    const isSameListing = currentListing.id.uuid === nextListing.id.uuid;
    const hasSamePrice = currentListing.attributes.price === nextListing.attributes.price;
    const hasSameActiveStatus = this.props.isActive === nextProps.isActive;
    const hasSameRefreshToken =
      this.props.mapComponentRefreshToken === nextProps.mapComponentRefreshToken;
    const hasSameSelectedCurrency = this.props.selectedCurrency === nextProps.selectedCurrency;

    return !(isSameListing && hasSamePrice && hasSameActiveStatus && hasSameRefreshToken && hasSameSelectedCurrency);
  };

  handleMouseEnter = () => {
    // Only trigger hover on desktop
    if (this.isMobile) return;

    const { listing, onListingClicked } = this.props;
    const currentListing = ensureListing(listing);

    // Clear any existing timeout
    if (this.hoverTimeout) {
      clearTimeout(this.hoverTimeout);
    }

    // Set timeout to trigger hover after delay
    this.hoverTimeout = setTimeout(() => {
      onListingClicked(currentListing);
    }, HOVER_DELAY);
  };

  handleMouseLeave = () => {
    // Clear hover timeout if mouse leaves before delay
    if (this.hoverTimeout) {
      clearTimeout(this.hoverTimeout);
      this.hoverTimeout = null;
    }
  };

  handleClick = () => {
    // Clear hover timeout on click
    if (this.hoverTimeout) {
      clearTimeout(this.hoverTimeout);
      this.hoverTimeout = null;
    }

    const { listing, onListingClicked } = this.props;
    const currentListing = ensureListing(listing);
    onListingClicked(currentListing);
  };

  render() {
    const {
      className,
      rootClassName,
      intl,
      listing,
      onListingClicked,
      isActive,
      isInfoCardSelection,
      config,
      selectedCurrency,
      exchangeRates
    } = this.props;
    const currentListing = ensureListing(listing);
    const { price } = currentListing.attributes;

    // Replace the static exchange rates with the real exchange rates.
    const convertedPrice = !!price ? convertPrice(exchangeRates, price.amount, price.currency, selectedCurrency) : null;

    // Create formatted price if currency is known or alternatively show just the unknown currency.
    const formattedPrice =
      convertedPrice && convertedPrice.currency === selectedCurrency
        ? formatMoney(intl, convertedPrice)
        : price?.currency
          ? price.currency
          : null;

    const shouldAnimate = isActive && !isInfoCardSelection;
    const classes = classNames(
      rootClassName || css.root,
      className,
      shouldAnimate ? css.activeJump : null
    );

    const priceLabelClasses = classNames(css.priceLabel, {
      [css.mapLabelActive]: isActive,
      [css.noPriceSetLabel]: !formattedPrice,
    });
    const caretClasses = classNames(css.caret, { [css.caretActive]: isActive });

    return (
      <button
        ref={this.rootRef}
        className={classes}
        onClick={this.handleClick}
        onMouseEnter={this.handleMouseEnter}
        onMouseLeave={this.handleMouseLeave}
      >
        <div className={css.caretShadow} />
        <div className={priceLabelClasses}>{formattedPrice}</div>
        <div className={caretClasses} />
      </button>
    );
  }
}

SearchMapPriceLabel.defaultProps = {
  className: null,
  rootClassName: null,
  isInfoCardSelection: false,
};

SearchMapPriceLabel.propTypes = {
  className: string,
  rootClassName: string,
  isInfoCardSelection: bool,
  listing: propTypes.listing.isRequired,
  onListingClicked: func.isRequired,
  config: object.isRequired,

  // from injectIntl
  intl: intlShape.isRequired,
};

export default injectIntl(SearchMapPriceLabel);
